import { FormattedMessage } from 'react-intl'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { isAfter } from 'date-fns'
import { NetworkStatus } from '@apollo/client'

import { Button } from 'components/v2/Button'
import Heading from 'components/v2/Heading'

import FormLayout from 'features/App/components/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { CreateRule } from 'features/Loyalty/components/CreateRule'
import { canRead } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { integerToCents } from 'utils/number'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { GET_LOYALTY_CAMPAIGN_RULE } from 'features/Loyalty/queries-legacy'
import { getCitySegmentsForMutation, mapLoyaltyCampaignRuleResponseToForm } from 'features/BaseRules/utils'
import {
  ADD_LOYALTY_CAMPAIGN_RULE_CITY_SEGMENTS,
  ADD_LOYALTY_CAMPAIGN_RULE_ROUTE_SERVICES,
  ADD_LOYALTY_CAMPAIGN_RULE_SALES_CHANNELS,
  CHANGE_LOYALTY_CAMPAIGN_RULE
} from 'features/Loyalty/mutations-legacy'
import { GET_LOYALTY_CAMPAIGN_COMPANY } from 'features/Loyalty/views/NewRule/queries'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { ICreateRuleForm } from 'features/Loyalty/components/CreateRule/types'
import { IEditRuleViewParams } from './types'
import { GTChangeLoyaltyCampaignRuleInput, GTLoyaltyCampaignRule } from 'gql/types.generated'

const EditRuleView = (): ReactElement => {
  const { campaignId = '', ruleId = '' } = useParams<IEditRuleViewParams>()
  const navigate = useNavigate()
  const {
    data: queryCompanyData,
    loading: loadingCompanyData,
    error: errorCompanyData
  } = useQuery(GET_LOYALTY_CAMPAIGN_COMPANY, {
    variables: {
      campaignId
    }
  })
  const {
    data,
    error: getRuleError,
    loading: loadingRule,
    refetch,
    networkStatus
  } = useQuery<{ loyaltyCampaignRule: GTLoyaltyCampaignRule }>(GET_LOYALTY_CAMPAIGN_RULE, {
    variables: {
      campaignId,
      ruleId
    },
    notifyOnNetworkStatusChange: true
  })
  const [editRule, { error, loading }] = useMutation(CHANGE_LOYALTY_CAMPAIGN_RULE)
  const [addLoyaltyRuleCitySegments, { error: addSegmentsError, loading: addSegmentsLoading }] = useMutation(
    ADD_LOYALTY_CAMPAIGN_RULE_CITY_SEGMENTS
  )
  const [addLoyaltyRuleSaleChannels, { error: addSaleChannelsError, loading: addSaleChannelsLoading }] = useMutation(
    ADD_LOYALTY_CAMPAIGN_RULE_SALES_CHANNELS
  )
  const [addLoyaltyRuleRouteServices, { error: addRouteServicesError, loading: addRouteServicesLoading }] = useMutation(
    ADD_LOYALTY_CAMPAIGN_RULE_ROUTE_SERVICES
  )

  const campaignStartDate = queryCompanyData?.loyaltyCampaign.startAt
    ? new Date(queryCompanyData?.loyaltyCampaign.startAt)
    : null

  const isCampaignStarted = campaignStartDate && isAfter(new Date(), campaignStartDate)

  async function handleEditSubmit(values: ICreateRuleForm): Promise<void> {
    const {
      ruleName,
      ruleDescription,
      ruleDetails,
      ruleHowToGetIt,
      ruleValidAt,
      allCitiesAndSegments,
      segments,
      salesChannels,
      totalBookingValue,
      allSegments,
      cityOrigins,
      cityDestinations,
      isReturn
    } = values

    const citySegments = getCitySegmentsForMutation({
      allCitiesAndSegments,
      allSegments,
      cityOrigins,
      cityDestinations,
      segments,
      isReturn
    })
    const routeServiceIds = values.services.map(({ id }) => id)
    const routeIds = values.routes.map(({ id }) => id)

    const details = {
      name: ruleName,
      description: ruleDescription,
      details: ruleDetails,
      howToGet: ruleHowToGetIt,
      validAt: ruleValidAt
    }
    const conditions = {
      salesChannelIDs: salesChannels.map(({ id }) => id),
      totalBookingsValue: !isEmpty(totalBookingValue) ? integerToCents(totalBookingValue) : undefined,
      citySegments: citySegments,
      routeServiceIDs: routeServiceIds,
      routeIDs: routeIds
    }
    if (isCampaignStarted) {
      await editRule({
        variables: {
          id: campaignId,
          rule: {
            id: ruleId,
            details
          }
        }
      })
      await addLoyaltyRuleCitySegments({
        variables: {
          id: campaignId,
          rule: {
            id: ruleId,
            conditions: {
              citySegments
            }
          }
        }
      })
      await addLoyaltyRuleSaleChannels({
        variables: {
          id: campaignId,
          rule: {
            id: ruleId,
            conditions: {
              salesChannelIDs: conditions.salesChannelIDs
            }
          }
        }
      })
      if (!isEmpty(routeServiceIds)) {
        await addLoyaltyRuleRouteServices({
          variables: {
            id: campaignId,
            rule: {
              id: ruleId,
              conditions: {
                routeIDs: routeIds,
                routeServiceIDs: routeServiceIds
              }
            }
          }
        })
      }
      navigate(`/loyalty/campaigns/${campaignId}/rules`)
    } else {
      const variables: GTChangeLoyaltyCampaignRuleInput = {
        id: campaignId,
        rule: {
          id: ruleId,
          details,
          conditions
        }
      }
      await editRule({
        variables
      })
      navigate(`/loyalty/campaigns/${campaignId}/rules`)
    }
  }
  const login = useLogin()
  if (!canRead(FEATURES.BOOKING, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BOOKING} />
  }
  let content = <></>
  if (networkStatus === NetworkStatus.refetch || loadingRule || loadingCompanyData) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (getRuleError || errorCompanyData) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="loyaltyCampaign.rules.edit.action.reload"
              defaultMessage="There was an error while loading the rule"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="loyaltyCampaign.rules.edit.action.reload" defaultMessage="Reload" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    const loyaltyCampaignRule = data?.loyaltyCampaignRule as GTLoyaltyCampaignRule
    content = (
      <CreateRule
        onSubmit={handleEditSubmit}
        loading={Boolean(loading || addSegmentsLoading || addSaleChannelsLoading || addRouteServicesLoading)}
        error={error || addSegmentsError || addSaleChannelsError || addRouteServicesError}
        loyaltyCampaignRule={mapLoyaltyCampaignRuleResponseToForm(loyaltyCampaignRule)}
        companyId={queryCompanyData?.loyaltyCampaign.company.id}
        isCampaignStarted={isCampaignStarted}
        isEditView
      />
    )
  }

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="loyaltyCampaign.rules.edit.title" defaultMessage="Edit rule" />
        </Heading.H3>
      </FormLayout.Header>

      <FormLayout.Content>
        <Card.Container>{content}</Card.Container>
      </FormLayout.Content>
    </div>
  )
}

export default withLastUrl(EditRuleView)
